import React, { createContext, useContext, useState, useEffect } from 'react';
import { ConfigProvider, theme } from 'antd';
import faIR from 'antd/locale/fa_IR';
import { themeConfig } from '../config/theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // بررسی تم ذخیره شده در localStorage
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  const [customColors, setCustomColors] = useState(() => {
    // بارگذاری رنگ‌های سفارشی از localStorage
    const savedColors = localStorage.getItem('customColors');
    return savedColors ? JSON.parse(savedColors) : {};
  });

  // ذخیره تم در localStorage و اضافه کردن class به document
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    // اضافه کردن class "dark" به html element برای Tailwind
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // ذخیره رنگ‌های سفارشی در localStorage
  useEffect(() => {
    localStorage.setItem('customColors', JSON.stringify(customColors));
  }, [customColors]);

  // دریافت رنگ‌های فعلی تم
  const getThemeColors = () => {
    const baseTheme = isDarkMode ? themeConfig.dark : themeConfig.light;
    return {
      ...baseTheme,
      primary: customColors.primaryColor || themeConfig.primaryColor,
      ...customColors,
    };
  };

  // تغییر تم
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // تنظیم رنگ‌های سفارشی
  const setThemeColors = (colors) => {
    setCustomColors((prev) => ({ ...prev, ...colors }));
  };

  // تنظیمات Ant Design ConfigProvider
  const antdTheme = {
    algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      // بدون این، کامپوننت‌های antd (تب، تایم‌لاین، دراپ‌داون، مودال و…) با فونت سیستم رندر می‌شوند و اعداد لاتین دیده می‌شوند
      fontFamily: "'CustomFont', 'Vazir', 'Tahoma', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      colorPrimary: customColors.primaryColor || themeConfig.primaryColor,
      colorSuccess: customColors.successColor || themeConfig.successColor,
      colorWarning: customColors.warningColor || themeConfig.warningColor,
      colorError: customColors.errorColor || themeConfig.errorColor,
      colorInfo: customColors.infoColor || themeConfig.infoColor,
      ...(isDarkMode
        ? {
            colorBgContainer: '#070B14',
            colorBgElevated: '#070B14',
            colorBgLayout: '#070B14',
            colorBorder: 'rgba(13, 136, 126, 0.22)',
            colorBorderSecondary: 'rgba(13, 136, 126, 0.14)',
            colorText: '#ffffff',
            colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
          }
        : {}),
    },
  };

  const value = {
    isDarkMode,
    toggleTheme,
    themeColors: getThemeColors(),
    setThemeColors,
    customColors,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider 
        theme={antdTheme}
        locale={faIR}
        direction="rtl"
      >
        <div className={`min-h-screen theme-transition ${isDarkMode ? 'panel-dark-bg text-white' : 'bg-white text-gray-900'}`}>
          {children}
        </div>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

